import { ComponentProps, useEffect, useMemo } from 'react';
import { FormProvider, useFormState } from 'react-hook-form';

import {
  EditorableSchema,
  FieldSchemaEditorSupportedTypes,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import FieldSchemaEditorV2 from 'dashboard/components-lib/FieldSchemaEditorForm/FieldSchemaEditorV2';
import { decorateFieldSchemas } from 'dashboard/components-lib/FieldSchemaEditorForm/utils';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { Identifier } from 'dashboard/models/identifier';
import Button from 'design-system/components/Button';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FieldSchemaEditorFormValues {
  fieldSchemas: EditorableSchema[];
  deactivateFieldSchemaKeys: string[];
}

type FieldSchemaEditorFormProps = Omit<
  ComponentProps<typeof FieldSchemaEditorV2>,
  'fieldNamePrefix'
> & {
  fieldSchemas: FieldSchemaEditorSupportedTypes[];
  identifiers?: Identifier[];
  isPending?: boolean;
  onSubmit: (formValues: FieldSchemaEditorFormValues) => void;
};

const FieldSchemaEditorForm: React.FC<FieldSchemaEditorFormProps> = ({
  fieldSchemas,
  identifiers,
  additionalHeaderActions,
  isPending,
  onSubmit,
  ...editorProps
}) => {
  const decoratedFieldSchemas = useMemo<EditorableSchema[]>(
    () => decorateFieldSchemas({ fieldSchemas, identifiers: identifiers ?? [] }),
    [fieldSchemas, identifiers],
  );
  const form = useFormSecurely<{
    fieldSchemas: EditorableSchema[];
    deactivateFieldSchemaKeys: string[];
  }>({
    defaultValues: {
      fieldSchemas: decoratedFieldSchemas,
      deactivateFieldSchemaKeys: [],
    },
  });

  const formState = useFormState(form);

  useEffect(() => {
    form.reset({ fieldSchemas: decoratedFieldSchemas, deactivateFieldSchemaKeys: [] });
  }, [decoratedFieldSchemas]);

  const isDirty = formState.isDirty;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <NavConfirmPrompt prevent={isDirty} />
        <FieldSchemaEditorV2
          {...editorProps}
          fieldNamePrefix='fieldSchemas'
          additionalHeaderActions={
            <>
              <Button
                id='field-schema-editor__save-field-button'
                variant='basic'
                type='submit'
                size='md'
                disabled={!isDirty}
                isPending={isPending}
              >
                Save
              </Button>
              {additionalHeaderActions}
            </>
          }
        />
      </form>
    </FormProvider>
  );
};

export default FieldSchemaEditorForm;
